/**
 * Created by katarinababic on 27.6.24.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { WebinarEntry } from '../../../Model/Explore/WebinarEntry';
import { ExploreContentViewWebinarReminder } from './ExploreContentViewWebinarReminder';
import { ExploreContentViewWebinarVideoPlayer } from './ExploreContentViewWebinarVideoPlayer';
import { LanguageDisclaimer } from '../LanguageDisclaimer';
import { Theme } from '../../../Theme/Theme';
import { MarkdownRenderer } from '../../Markdown/MarkdownRenderer';
import { useLocalized } from '../../../Hooks/useLocalized';
import { ShareButton } from '../../ShareButton';
import { ScheduleButton } from '../../ScheduleButton';
import { ScheduleModal } from '../../ScheduleModal/ScheduleModal';
import { useContentMetaTags } from '../../../Hooks/useContentMetaTags';
import { getMediaMediumOrSmallest } from '../../../Model/Media/Media';
import { useLanguage } from '../../../Hooks/useLanguage';
import { EventEntry, EventEntryApi } from '../../../Model/Coach/Schedule/EventEntry';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageContainer = styled.div`
  min-height: 100vh;
  background: #fafafa;
`;

const VideoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 32px 0;

  @media (max-width: 768px) {
    padding: 16px 16px 0;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  align-items: center;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 32px 64px;
  animation: ${fadeIn} 0.6s ease-out;

  @media (max-width: 768px) {
    padding: 24px 16px 48px;
  }
`;

const ContentCard = styled.div`
  display: flex;
  gap: 32px;
  align-items: flex-start;
  width: 100%;
  background: white;
  border-radius: 24px;
  padding: 32px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 24px;
    gap: 24px;
  }
`;

const TextContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${Theme.Colors.textColor};
  margin: 0;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const GradientLine = styled.div`
  width: min(200px, 40%);
  height: 4px;
  border-radius: 4px;
  background: ${Theme.Colors.primary};
`;

const Description = styled.div`
  font-size: 1rem;
  line-height: 1.7;
  color: ${Theme.Colors.charcoal};

  p {
    margin: 0 0 16px 0;

    &:last-child {
      margin-bottom: 0;
    }
  }

  a {
    color: ${Theme.Colors.primary};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const SideContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 280px;
  max-width: 300px;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
  }
`;

const PresenterCard = styled.div`
  background: ${Theme.Colors.lightestGrey};
  border-radius: 20px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const PresenterAvatar = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  object-fit: cover;
  margin-bottom: 16px;
  border: 4px solid white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const PresenterName = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: ${Theme.Colors.textColor};
  margin: 0 0 8px 0;
`;

const PresenterRole = styled.div`
  font-size: 0.875rem;
  color: ${Theme.Colors.charcoal};
  line-height: 1.5;
`;

const ReminderCard = styled.div`
  background: ${Theme.Colors.lightestGrey};
  border-radius: 20px;
  padding: 24px;
`;

export type ExploreContentViewWebinarProps = {
  entry: WebinarEntry;
};

export const ExploreContentViewWebinar: React.FC<ExploreContentViewWebinarProps> = observer(({ entry }) => {
  const { nextDate } = entry;
  const { language: userLanguage } = useLanguage();
  const host = entry.hosts?.[0];

  const title = useLocalized(entry.name);
  const description = useLocalized(entry.description);

  useContentMetaTags({ title, description });

  const hostName = host?.name;
  const hostDescription = host?.translatedDescription;
  const hostAvatarUrl = host?.image ? getMediaMediumOrSmallest(host.image) : undefined;

  const hasVideoInUserLanguage = (entry.videos ?? []).some(
    (v) => v.language?.toLowerCase() === userLanguage.toLowerCase(),
  );
  const showReminder = nextDate && !hasVideoInUserLanguage;

  const [showSchedule, setShowSchedule] = React.useState(false);
  const [eventEntry, setEventEntry] = React.useState<EventEntry | undefined>(undefined);

  const loadEvent = React.useCallback(() => {
    EventEntryApi.findParent({ objectType: entry.type, objectId: entry.id }).then((e) => setEventEntry(e));
  }, [entry.type, entry.id]);

  React.useEffect(() => {
    loadEvent();
  }, [loadEvent]);

  const handleShowScheduleModal = React.useCallback(() => setShowSchedule(true), []);
  const handleCloseScheduleModal = React.useCallback(() => {
    setShowSchedule(false);
    loadEvent();
  }, [loadEvent]);

  return (
    <PageContainer>
      <VideoSection>
        <ExploreContentViewWebinarVideoPlayer entry={entry} />
        {!entry.nextStartDate && !hasVideoInUserLanguage && (
          <LanguageDisclaimer availableLanguages={entry.availableLanguages} />
        )}
      </VideoSection>

      <ContentContainer>
        <ContentCard>
          <TextContent>
            <Title>{title}</Title>
            <GradientLine />
            <Description>
              <MarkdownRenderer text={description} />
            </Description>
          </TextContent>

          <SideContent>
            <ShareButton data={{ title, description, contentType: 'webinar', contentId: entry.id }} />
            <ScheduleButton event={eventEntry} onClick={handleShowScheduleModal} />
            {showReminder && (
              <ReminderCard>
                <ExploreContentViewWebinarReminder entry={entry} />
              </ReminderCard>
            )}
            {hostName && hostAvatarUrl && (
              <PresenterCard>
                <PresenterAvatar src={hostAvatarUrl} alt={hostName} />
                <PresenterName>{hostName}</PresenterName>
                {hostDescription && (
                  <PresenterRole>
                    <MarkdownRenderer text={hostDescription} />
                  </PresenterRole>
                )}
              </PresenterCard>
            )}
          </SideContent>
        </ContentCard>
      </ContentContainer>

      {showSchedule && (
        <ScheduleModal
          key={eventEntry?.id ?? entry.id}
          exploreEntry={entry}
          parentEvent={eventEntry}
          currentEvent={eventEntry}
          disableRecurring={false}
          onSchedule={handleCloseScheduleModal}
          onDelete={handleCloseScheduleModal}
        />
      )}
    </PageContainer>
  );
});
